import { AudioPlayer, createAudioPlayer } from 'expo-audio';
import { sounds } from '@/assets/sounds';
import { SoundEnum } from '@matap/shared';
import { Platform } from 'react-native';

const playLocalVoice = true;
let lastUnlockTime = 0;
let webAudioContext: AudioContext | null = null;

// 🚀 Added: Cache for our audio buffers to prevent redundant network requests and decoding
const audioBufferCache = new Map<SoundEnum, AudioBuffer>();

export function unlockSafariAudio() {
  if (Platform.OS !== 'web') return;

  const now = Date.now();
  if (now - lastUnlockTime < 5 * 60 * 1000) return;

  try {
    if (!webAudioContext) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        webAudioContext = new AudioContextClass();
      }
    }

    if (webAudioContext) {
      if (webAudioContext.state === 'suspended') {
        const resumePromise = webAudioContext.resume();
        if (resumePromise && typeof resumePromise.catch === 'function') {
          resumePromise.catch((err) => {
            console.warn('AudioContext resume deferred:', err.message);
            lastUnlockTime = 0;
          });
        }
      }

      const buffer = webAudioContext.createBuffer(1, 1, 22050);
      const source = webAudioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(webAudioContext.destination);

      if (source.start) {
        source.start(0);
      } else if ((source as any).noteOn) {
        (source as any).noteOn(0);
      }
    }

    // NOTE: Removed the 10-second setTimeout test here.
    // The unlock function should strictly be a silent initialization tool.

    lastUnlockTime = now;
    console.log('Web Audio Context Unlocked!');
  } catch (error) {
    console.warn('Safari AudioContext unlock failed:', error);
    lastUnlockTime = 0;
  }
}

export function playLocalSound(soundName: SoundEnum, delayInSeconds: number = 0) {
  const endListeners: (() => void)[] = [];
  let isCancelled = false; // Prevents playback if stop() is called during async phases

  if (!canPlayLocalSound()) {
    console.warn('[Audio] Ignored playing local sound because we are not prepared');
    return {
      setOnEndListener: (cb: () => void) => {},
      stop: () => {},
    };
  }
  // ---------------------------------------------------------
  // 🌐 WEB AUDIO API OVERRIDE
  // ---------------------------------------------------------
  // Only completely bail if the context doesn't exist, we will resume it later if it's suspended
  if (Platform.OS === 'web') {
    let sourceNode: AudioBufferSourceNode | null = null;

    const playBuffer = async (audioBuffer: AudioBuffer) => {
      if (isCancelled) return;

      // 🚨 Attempt to wake up the context if it is suspended
      if (webAudioContext!.state === 'suspended') {
        try {
          await webAudioContext!.resume();
        } catch (err) {
          console.warn('Failed to resume AudioContext:', err);
        }
      }

      // Check one more time in case stop() was called during the resume await
      if (isCancelled) return;

      sourceNode = webAudioContext!.createBufferSource();
      sourceNode.buffer = audioBuffer;
      sourceNode.connect(webAudioContext!.destination);

      sourceNode.onended = () => {
        endListeners.forEach((cb) => cb());
      };

      sourceNode.start(webAudioContext!.currentTime + delayInSeconds);
    };

    // Check cache first
    if (audioBufferCache.has(soundName)) {
      playBuffer(audioBufferCache.get(soundName)!);
    } else {
      // Fetch and decode if not in cache
      fetch(sounds[soundName])
        .then((res) => res.arrayBuffer())
        .then((arrayBuffer) => {
          return new Promise<AudioBuffer>((resolve, reject) => {
            // Support both modern Promise-based and legacy callback-based Safari decoding
            const decodeResult = webAudioContext!.decodeAudioData(
              arrayBuffer,
              (decoded) => resolve(decoded),
              (err) => reject(err),
            );
            if (decodeResult) {
              decodeResult.then(resolve).catch(reject);
            }
          });
        })
        .then((audioBuffer) => {
          audioBufferCache.set(soundName, audioBuffer); // Cache it!
          playBuffer(audioBuffer);
        })
        .catch((err) => console.warn('Web Audio API playback failed:', err));
    }

    return {
      setOnEndListener: (cb: () => void) => endListeners.push(cb),
      stop: () => {
        isCancelled = true; // Prevent playback if still fetching/decoding
        try {
          sourceNode?.stop();
        } catch (e) {
          /* silent catch */
        }
      },
    };
  }

  // ---------------------------------------------------------
  // 📱 NATIVE EXPO AUDIO FALLBACK (iOS / Android)
  // ---------------------------------------------------------
  let track: AudioPlayer | null = null;
  let statusSubscription: { remove: () => void } | null = null;
  let unloaded = false;
  let hasTriggeredPlay = false;

  if (playLocalVoice) {
    track = createAudioPlayer(sounds[soundName], { updateInterval: 200 });
    if (unloaded) {
      track.remove();
      track = null;
    } else {
      statusSubscription = track.addListener('playbackStatusUpdate', (status) => {
        if (status.isLoaded && !hasTriggeredPlay) {
          hasTriggeredPlay = true;
          try {
            if (delayInSeconds > 0) {
              setTimeout(() => track?.play(), delayInSeconds * 1000);
            } else {
              track?.play();
            }
          } catch (e) {
            console.warn('Audio blocked:', e);
          }
        }

        if (status.isLoaded && status.didJustFinish) {
          statusSubscription?.remove();
          track?.remove();
          track = null;
          endListeners.forEach((l) => l());
        }
      });
    }
  }

  return {
    setOnEndListener: (cb: () => void) => {
      endListeners.push(cb);
    },
    stop: () => {
      statusSubscription?.remove();
      statusSubscription = null;
      if (track) {
        track.pause();
        track.remove();
        track = null;
      } else {
        unloaded = true;
      }
    },
  };
}

export function canPlayLocalSound(): boolean {
  if (Platform.OS === 'web') {
    if (!webAudioContext || webAudioContext.state === 'closed') {
      console.warn('[Audio] Web Audio Context is not initialized or is closed.');
      return false;
    }
  }

  return true;
}
